/**
 * A framework as Linear draws a project: its health, how much of its
 * controls are addressed, where its publications stand, and how busy its
 * regulators have been. Pure — computed from data the page already loads.
 *
 * @module components/regulation/monitor/frameworkRollup
 */
import type { GovernanceFramework } from "../../../state/governance";
import type { RegulatoryUpdate } from "../../../state/policies";
import {
  DAY,
  groupPublications,
  isQuiet,
  publicationState,
  type PublicationState,
} from "./publications";

export type Health = "on_track" | "at_risk" | "off_track";

/** Controls addressed out of controls held, per regime catalogue id. */
export interface RegimeCoverage {
  readonly controlsSatisfied: number;
  readonly controlTotal: number;
}

export interface FrameworkRollup {
  readonly health: Health;
  /** Why, in the reader's words — the health's tooltip. */
  readonly reasons: ReadonlyArray<string>;
  readonly controls: { readonly satisfied: number; readonly total: number };
  /** Rows (folded runs count once, as in the list) per state. */
  readonly states: Readonly<Record<PublicationState, number>>;
  /** Publications in the last thirty days, bucketed like Linear's activity chevrons. */
  readonly activity: 0 | 1 | 2 | 3;
  readonly recent: number;
}

export const ACTIVITY_WINDOW_DAYS = 30;

/** Whether a publication is filed under one of these regimes — the one rule both the table and the list use. */
export const filedUnder = (update: RegulatoryUpdate, regimeIds: ReadonlyArray<string>): boolean =>
  update.frameworkId != null && regimeIds.includes(update.frameworkId);

/** The publications filed under one of the framework's regimes. */
export function frameworkPublications(
  framework: Pick<GovernanceFramework, "regimes">,
  publications: ReadonlyArray<RegulatoryUpdate>,
): RegulatoryUpdate[] {
  const regimes = framework.regimes.map((regime) => regime.id);
  return publications.filter((update) => filedUnder(update, regimes));
}

export const activityLevel = (recent: number): 0 | 1 | 2 | 3 =>
  recent === 0 ? 0 : recent <= 2 ? 1 : recent <= 5 ? 2 : 3;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function frameworkRollup(
  framework: GovernanceFramework,
  coverage: ReadonlyMap<string, RegimeCoverage>,
  publications: ReadonlyArray<RegulatoryUpdate>,
  now: number,
): FrameworkRollup {
  const own = frameworkPublications(framework, publications);
  const states: Record<PublicationState, number> = {
    findings: 0,
    decide: 0,
    handled: 0,
    proposed_not_applicable: 0,
    not_applicable: 0,
  };
  for (const group of groupPublications(own)) states[publicationState(group.lead)] += 1;

  let satisfied = 0;
  let total = 0;
  for (const regime of framework.regimes) {
    const held = coverage.get(regime.id);
    satisfied += held?.controlsSatisfied ?? 0;
    total += held?.controlTotal ?? 0;
  }

  const recent = own.filter(
    (update) => now - Date.parse(update.publishedAt ?? update.detectedAt) <= ACTIVITY_WINDOW_DAYS * DAY,
  ).length;

  // Off track: its documents are out of line with something already published.
  // At risk: a decision is owed, or nobody (or only a quiet source) is watching.
  const offTrack: string[] = [];
  const atRisk: string[] = [];
  if (framework.openFindings > 0) offTrack.push(`${plural(framework.openFindings, "finding", "findings")} open in its documents`);
  if (states.decide > 0) atRisk.push(`${plural(states.decide, "publication needs", "publications need")} a decision`);
  if (framework.regimes.length > 0 && framework.sources.filter((source) => source.enabled).length === 0) {
    atRisk.push("No source watches its regimes");
  }
  const quiet = framework.sources.filter((source) => isQuiet(source, now));
  if (quiet.length > 0) atRisk.push(`${quiet.map((source) => source.name.split(" — ")[0]).join(", ")} gone quiet`);

  const health: Health = offTrack.length > 0 ? "off_track" : atRisk.length > 0 ? "at_risk" : "on_track";
  return {
    health,
    reasons: [...offTrack, ...atRisk].length > 0 ? [...offTrack, ...atRisk] : ["Nothing is waiting on you"],
    controls: { satisfied, total },
    states,
    activity: activityLevel(recent),
    recent,
  };
}
